import { applyScheduleAttendanceMutationToRaw } from '@common/domain/schedule-attendance-mutation';
import type {
  ScheduleAttendanceMutationRequest,
  ScheduleAttendanceMutationResult,
} from '@common/domain/schedule-attendance-mutation';
import type { ScheduleGreeting } from '@common/domain/schedule-greetings';
import { DEFAULT_SCHEDULE_RESERVE_LIMIT, isScheduleAtCapacity } from '@common/domain/schedule-reserve-limit';
import { addWaitListEntry, removeWaitListMember } from '@common/domain/attendance-mutations';
import { promoteNextFromWaitList } from '@common/domain/wait-list-promotion';
import { sortWaitListByAddedAt } from '@common/domain/wait-list-sort';
import { MAS_Accounts_Service } from '../services';
import { MAS_Schedules_Service } from '../services';
import { AnyObject, masIdsEqual } from '../utilities';
import { MASAccountModel } from './mas-accounts.model';

/**
 * @deprecated Use MASScheduleExtrasModel instead.
 */
export interface Classes {
  accountId: string;
  displayName: string;
  programId: string;
  programName: string;
  reserveLimit: number;
  schedules: ScheduleElementType[];
  tableId: string;
}

/**
 * @deprecated Use MASScheduleExtrasModel instead.
 */
export interface DisplaySchedule extends Schedule {
  startTime: string;
  endTime: string;
}

/**
 * @deprecated Use MASScheduleExtrasModel instead.
 */
export interface ScheduleElementType {
  attendance: ScheduleAttendance[];
  css: 'selected' | 'unselected';
  cycleName: string;
  disable: boolean;
  enrolled: boolean;
  icon: 'pi pi-times-circle' | 'pi pi-check-circle';
  id: string;
  reserveLimit: number;
  startDateTime: string;
  testingLimits: number;
}

export interface Schedule {
  acknowledgements?: { accountId: string }[];
  attendance?: ScheduleAttendance[];
  /** Dashboard parents: staff greetings for this class (parent + user). */
  greetings?: ScheduleGreeting[];
  buttonText?: string;
  category?: string;
  cycleName: string;
  description?: string;
  end: { dateTime: string; timeZone: string };
  eventType: string;
  guest?: {
    sponsor: string;
    id: string;
    name: string;
    reserved?: boolean;
  }[];
  guestLimit?: number;
  id: string;
  image?: string;
  instructorId?: string;
  instructorName?: string;
  /** Present when row was imported from `mas-lessons` (id `ml-{legacyId}`). */
  legacyLessonId?: string;
  notifications?: Notifications[];
  optionReserve?: boolean;
  /** Classes, tests, and lessons require a program; facility events do not. */
  programId?: string;
  recurringEventId?: string | null;
  reserveLimit?: number;
  start: { dateTime: string; timeZone: string };
  summary: string;
  url?: string;
  waitList?: WaitList[];
}

export interface WaitList {
  accountId: string;
  addedAt: string; // ISO timestamp - used to determine wait-list order (FIFO)
  email?: string;
  eventId: string;
  name: string;
  notifications?: Notifications | null; // snapshot of parent's notification prefs at join time
  phone?: string;
}

export interface ScheduleAttendance {
  attendanceTotal?: number;
  attended: boolean;
  id: string;
  name: string;
  notifications?: Notifications | null;
  // Set true when this attendance entry was created by promoting someone off
  // the wait list. Backend functions can use this flag to send a one-time
  // "you got in!" notification (then clear the flag, or ignore subsequent reads).
  promotedFromWaitList?: boolean;
  reserved: boolean;
  tournaments?: string;
  childAccountModel?: MASAccountModel | null;
  /** @deprecated Prefer {@link parentAccountModels}; kept as first linked parent. */
  parentAccountModel?: MASAccountModel | null;
  parentAccountModels?: MASAccountModel[];
  /** Trial students only: total classes attended (all programs). */
  classesAttended?: number | null;
  /** Trial students only: short end-date label (e.g. "Aug 10"), or "-" when missing. */
  endDateDisplay?: string | null;
  school?: string | null;
}

export interface Notifications {
  email?: string;
  id: string;
  name: string;
  phone?: string;
}

/**
 * View-model for a schedule document.
 *
 * **Preview vs persist**
 * - **Preview** (in-memory only): {@link previewAttendanceMutation} and deprecated mutators that delegate to
 *   `common/app/domain/schedule-attendance-mutation.ts`.
 * - **Persist** (authoritative): `enrollInScheduleAtomic` → {@link applyScheduleAtomicPatchToRaw} on `raw`.
 *   Do not call {@link save} for hot-path attendance/wait-list changes.
 */
export class MASScheduleModel {
  [x: string]: any;
  #data: Schedule;

  constructor(
    data: Schedule,
    private scheduleService: MAS_Schedules_Service,
  ) {
    this.#data = data;
  }

  // ==========================
  // 📦 Public API
  // ==========================

  get raw(): Schedule {
    return this.#data;
  }

  // Common field accessors
  get id(): string {
    return this.#data.id;
  }

  get programId(): string {
    return this.#data.programId ?? '';
  }

  get summary(): string {
    return this.#data.summary;
  }

  get cycleName(): string {
    return this.#data.cycleName;
  }

  get attendance(): ScheduleAttendance[] | undefined {
    return this.#data.attendance;
  }

  set attendance(attendance: ScheduleAttendance[] | undefined) {
    this.#data.attendance = attendance;
  }

  get greetings(): ScheduleGreeting[] {
    return this.#data.greetings ?? [];
  }

  set greetings(greetings: ScheduleGreeting[] | undefined) {
    this.#data.greetings = greetings;
  }

  get start(): { dateTime: string; timeZone: string } {
    return this.#data.start;
  }

  get end(): { dateTime: string; timeZone: string } {
    return this.#data.end;
  }

  get eventType(): string {
    return this.#data.eventType;
  }

  get notifications(): Notifications[] | undefined {
    return this.#data.notifications;
  }

  get recurringEventId(): string | null | undefined {
    return this.#data.recurringEventId;
  }

  get waitList(): WaitList[] | undefined {
    return this.#data.waitList;
  }

  set waitList(waitList: WaitList[] | undefined) {
    this.#data.waitList = waitList;
  }

  clone(): MASScheduleModel {
    const clonedRaw: Schedule = JSON.parse(JSON.stringify(this.raw));
    return new MASScheduleModel(clonedRaw, this.scheduleService);
  }

  toJSON(): Schedule {
    return this.#data;
  }

  /**
   * Applies a domain attendance mutation to this model's `raw` arrays (preview / UI simulation).
   * Does not persist — use `enrollInScheduleAtomic` for authoritative writes.
   */
  previewAttendanceMutation(
    request: Omit<ScheduleAttendanceMutationRequest, 'scheduleId'> & {
      scheduleId?: string;
    },
    reserveLimit?: number,
  ): ScheduleAttendanceMutationResult<ScheduleAttendance, WaitList> {
    const resolvedReserveLimit = request['reserveLimit'] ?? reserveLimit;
    return applyScheduleAttendanceMutationToRaw(this.raw, {
      ...request,
      scheduleId: request.scheduleId ?? this.id,
      reserveLimit: resolvedReserveLimit,
    });
  }

  /**
   * @deprecated Client schedule writes are disabled. Use `enrollInScheduleAtomic` for persistence.
   */
  async save(merge: boolean = true): Promise<Schedule> {
    return await this.scheduleService.persistScheduleRaw(this.raw, merge);
  }

  // ==========================
  // 🛠️ Mutators (preview — delegate to domain)
  // ==========================

  /**
   * @deprecated Use {@link previewAttendanceMutation} with `mode: 'syncMemberName'` or atomic `syncMemberName`.
   * @see `common/app/domain/attendance-mutations.ts` — `syncAttendanceMemberName`
   */
  correctAttendanceName(account: MASAccountModel): this {
    this.previewAttendanceMutation({
      mode: 'syncMemberName',
      memberId: account.raw.id,
      memberName: account.fullName,
    });
    return this;
  }

  /**
   * @deprecated Use {@link previewAttendanceMutation} with `mode: 'syncNotificationName'` or atomic `syncNotificationName`.
   * @see `common/app/domain/attendance-mutations.ts` — `syncAttendanceNotificationName`
   */
  correctAttendanceNotificationName(account: MASAccountModel): this {
    this.previewAttendanceMutation({
      mode: 'syncNotificationName',
      memberId: account.raw.id,
      memberName: account.fullName,
    });
    return this;
  }

  /**
   * @deprecated Use {@link previewAttendanceMutation} with `mode: 'remove'` or atomic `remove`.
   * @see `common/app/domain/schedule-attendance-remove.ts` — `removeAttendanceWithWaitListPromotion`
   */
  removeAttendance(account: MASAccountModel, reserveLimit: number = DEFAULT_SCHEDULE_RESERVE_LIMIT): this {
    this.previewAttendanceMutation(
      { mode: 'remove', memberId: account.raw.id },
      reserveLimit,
    );
    return this;
  }

  isEnrolled(accountId: string): boolean {
    return !!this.raw.attendance?.some((att) => masIdsEqual(att.id, accountId));
  }

  /**
   * @deprecated Use {@link previewAttendanceMutation} with `mode: 'upsert'` / `'remove'` or atomic enroll.
   * @see `common/app/domain/schedule-attendance-mutation.ts` — `applyScheduleAttendanceMutation`
   */
  toggleAttendance(
    member: {
      id: string;
      name: string;
      notifications?: Notifications | null;
      attended?: boolean;
      reserved?: boolean;
    },
    reserveLimit: number = DEFAULT_SCHEDULE_RESERVE_LIMIT,
  ): boolean {
    if (this.isEnrolled(member.id)) {
      this.previewAttendanceMutation({ mode: 'remove', memberId: member.id }, reserveLimit);
      return false;
    }

    const result = this.previewAttendanceMutation(
      {
        mode: 'upsert',
        memberId: member.id,
        memberName: member.name,
        notifications: member.notifications ?? undefined,
        attended: member.attended,
        reserved: member.reserved,
      },
      reserveLimit,
    );
    return !result.rejected;
  }

  // ==========================
  // 📝 Wait-list helpers
  // ==========================

  /** True when current attendance count meets or exceeds the given reserveLimit. */
  isFull(reserveLimit: number): boolean {
    return isScheduleAtCapacity(this.raw.attendance?.length ?? 0, reserveLimit);
  }

  isOnWaitList(accountId: string): boolean {
    return !!this.raw.waitList?.some((w) => masIdsEqual(w.accountId, accountId));
  }

  waitListPosition(accountId: string): number {
    const list = this.sortedWaitList();
    const idx = list.findIndex((w) => masIdsEqual(w.accountId, accountId));
    return idx === -1 ? -1 : idx + 1;
  }

  /** Returns wait list sorted FIFO by addedAt (earliest first). */
  sortedWaitList(): WaitList[] {
    return sortWaitListByAddedAt(this.raw.waitList ?? []);
  }

  /**
   * @deprecated Use {@link previewAttendanceMutation} with `mode: 'waitListAdd'` or atomic `waitListAdd`.
   * @see `common/app/domain/attendance-mutations.ts` — `addWaitListEntry`
   */
  addToWaitList(entry: WaitList): boolean {
    const added = addWaitListEntry(this.raw.waitList ?? [], entry);
    if (added.changed) {
      this.raw.waitList = added.waitList;
    }
    return added.changed;
  }

  /**
   * @deprecated Use {@link previewAttendanceMutation} with `mode: 'waitListRemove'` or atomic `waitListRemove`.
   * @see `common/app/domain/attendance-mutations.ts` — `removeWaitListMember`
   */
  removeFromWaitList(accountId: string): WaitList | undefined {
    const before = this.raw.waitList ?? [];
    const entry = before.find((w) => masIdsEqual(w.accountId, accountId));
    const removed = removeWaitListMember(before, accountId);
    if (!removed.changed) {
      return undefined;
    }
    this.raw.waitList = removed.waitList;
    return entry;
  }

  /**
   * Removes and returns the oldest wait-list entry (FIFO by addedAt).
   * @deprecated Prefer {@link previewAttendanceMutation} or domain `sortWaitListByAddedAt` + `removeWaitListMember`.
   */
  popFirstFromWaitList(): WaitList | undefined {
    const sorted = this.sortedWaitList();
    const first = sorted[0];
    if (!first) return undefined;
    this.removeFromWaitList(first.accountId);
    return first;
  }

  /**
   * If the schedule has room and someone is on the wait list, move the
   * oldest wait-list entry into attendance and tag it with `promotedFromWaitList: true`.
   *
   * @deprecated Prefer atomic `remove` (auto-promotes) or domain `promoteNextFromWaitList`.
   * @see `common/app/domain/wait-list-promotion.ts`
   */
  promoteFromWaitList(reserveLimit: number): WaitList | undefined {
    const step = promoteNextFromWaitList(
      this.id,
      this.raw.attendance ?? [],
      this.raw.waitList ?? [],
      reserveLimit,
    );
    if (!step.changed) {
      return undefined;
    }
    this.raw.attendance = step.attendance as ScheduleAttendance[];
    this.raw.waitList = step.waitList;
    return step.promoted as WaitList | undefined;
  }

  // ==========================
  // 🔍 Diff Utilities
  // ==========================

  /**
   * Returns a deep object of differences between this model and another model.
   */
  diffRaw(otherRaw: Schedule): AnyObject {
    return this._findDifferences(this.raw, otherRaw) || {};
  }

  diffSummaryRaw(otherRaw: Schedule): string[] {
    const changedPaths: string[] = [];
    this._findDifferencesSummary(this.raw, otherRaw, [], changedPaths);
    return changedPaths;
  }

  hasChangesRaw(otherRaw: Schedule): boolean {
    return this.diffSummaryRaw(otherRaw).length > 0;
  }

  // ==========================
  // 🔒 Private Helpers
  // ==========================

  private _findDifferences(a: any, b: any): any {
    if (
      typeof a !== 'object' ||
      typeof b !== 'object' ||
      a === null ||
      b === null
    ) {
      return a !== b ? a : undefined;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      return JSON.stringify(a) !== JSON.stringify(b) ? a : undefined;
    }

    const result: AnyObject = {};
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);

    for (const key of keys) {
      const diff = this._findDifferences(a[key], b[key]);
      if (diff !== undefined) {
        result[key] = diff;
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private _findDifferencesSummary(
    a: any,
    b: any,
    path: string[] = [],
    changedPaths: string[],
  ) {
    if (
      typeof a !== 'object' ||
      typeof b !== 'object' ||
      a === null ||
      b === null
    ) {
      if (a !== b) changedPaths.push(path.join('.'));
      return;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      if (JSON.stringify(a) !== JSON.stringify(b))
        changedPaths.push(path.join('.'));
      return;
    }

    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    for (const key of keys) {
      this._findDifferencesSummary(
        a[key],
        b[key],
        [...path, key],
        changedPaths,
      );
    }
  }
}

export interface scheduleExtras {
  attendanceCount?: number;
  css?: 'selected' | 'unselected' | 'waitlisted' | 'full';
  disable?: boolean;
  duplicates?: boolean;
  enrolled?: boolean;
  full?: boolean; // class is at capacity (attendance.length >= reserveLimit)
  icon?: 'pi pi-times-circle' | 'pi pi-check-circle' | 'pi pi-clock';
  memberReservedIndex?: number;
  onWaitList?: boolean; // current account is on the schedule's wait list
  optionReserve?: boolean;
  reservationCount?: number;
  reserveLimit?: number;
  waitListPosition?: number; // 1-based position when onWaitList === true
}

/** How to order rows after `enrichAttendance` loads member fields (e.g. `school`). */
export type AttendanceSortMode = 'name' | 'schoolThenName';

export class MASScheduleExtrasModel {
  startTime?: string;
  endTime?: string;

  constructor(
    public schedule: MASScheduleModel,
    public extras: scheduleExtras = {},
  ) {}

  get id(): string {
    return this.schedule.id;
  }

  get summary(): string {
    return this.schedule.summary;
  }

  get attendance(): ScheduleAttendance[] | undefined {
    return this.schedule.attendance;
  }

  set attendance(attendance: ScheduleAttendance[] | undefined) {
    this.schedule.raw.attendance = attendance;
  }

  get cycleName(): string {
    return this.schedule.cycleName;
  }

  async enrichAttendance(
    accountsService: MAS_Accounts_Service,
    reservePrograms: string[],
    logError: (context: string, error: any) => void,
    sortMode: AttendanceSortMode = 'schoolThenName',
  ): Promise<void> {
    const schedule = this.schedule.raw;
    const attendance: ScheduleAttendance[] | undefined = schedule.attendance;

    if (!attendance) return;

    const promises = attendance.map(async (member) => {
      if (!member.id) {
        member.attendanceTotal = 0;
        member.tournaments = 'whitesmoke';
        member.school = 'none';
        return;
      }

      try {
        const [tournaments, school, attendanceHistory] = await Promise.all([
          this.getTournaments(accountsService, member.id, logError),
          this.getSchool(accountsService, member.id, logError),
          this.getTotalAttendance(
            accountsService,
            member.id,
            reservePrograms,
            schedule.cycleName,
            logError,
          ),
        ]);

        member.school = school || 'none';
        member.tournaments = tournaments || 'none';
        member.attendanceTotal = attendanceHistory.length;
      } catch (error) {
        logError('MASScheduleExtrasModel.enrichAttendance.member', error);
      }
    });

    try {
      await Promise.all(promises);
      const cmp = (x: string, y: string) =>
        x.localeCompare(y, undefined, { sensitivity: 'base' });
      if (sortMode === 'name') {
        attendance.sort((a, b) => cmp(a.name || '', b.name || ''));
      } else {
        attendance.sort(
          (a, b) =>
            cmp(a.school || '', b.school || '') ||
            cmp(a.name || '', b.name || ''),
        );
      }
    } catch (error) {
      logError('MASScheduleExtrasModel.enrichAttendance.PromiseAll', error);
    }
  }

  private async getTotalAttendance(
    accountsService: MAS_Accounts_Service,
    memberId: string,
    reservePrograms: string[],
    cycleName: string,
    logError: (context: string, error: any) => void,
  ): Promise<any[]> {
    try {
      const attendance = await accountsService.getAccountAttendanceCount(
        memberId,
        reservePrograms,
        cycleName,
      );
      return attendance || [];
    } catch (error) {
      logError('MASScheduleExtrasModel.getTotalAttendance', error);
      return [];
    }
  }

  private async getTournaments(
    accountsService: MAS_Accounts_Service,
    memberId: string,
    logError: (context: string, error: any) => void,
  ): Promise<string> {
    try {
      const account = await accountsService.getAccountById(memberId);
      return account?.mas?.tournaments || 'none';
    } catch (error) {
      logError('MASScheduleExtrasModel.getTournaments', error);
      return 'none';
    }
  }

  private async getSchool(
    accountsService: MAS_Accounts_Service,
    memberId: string,
    logError: (context: string, error: any) => void,
  ): Promise<string> {
    try {
      const account = await accountsService.getAccountById(memberId);
      return account?.mas?.accountSettings.school || 'none';
    } catch (error) {
      logError('MASScheduleExtrasModel.getTournaments', error);
      return 'none';
    }
  }
}
